import {useCallback, useEffect, useRef, useState} from 'react';
import {followApi} from '../../../../services/followApi';
import {TalentRecommendation, talentSearchApi} from '../../../../services/talentSearchApi';

const RECOMMENDATION_POOL_SIZE = 6;
const VISIBLE_RECOMMENDATION_COUNT = 3;

const isRequestCanceled = (error: unknown) => (
  error instanceof Error && (error.name === 'CanceledError' || error.name === 'AbortError')
);

export const useHomeSuggestions = (
  token: string | null,
  onFollowingCountDelta?: (delta: number) => void,
) => {
  const [recommendations, setRecommendations] = useState<TalentRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [followError, setFollowError] = useState<string | null>(null);
  const [pendingFollowIds, setPendingFollowIds] = useState<Record<string, boolean>>({});
  const pendingFollowIdsRef = useRef<Record<string, boolean>>({});
  const refreshControllerRef = useRef<AbortController | null>(null);
  const followControllersRef = useRef<Record<string, AbortController>>({});

  const loadRecommendations = useCallback(async (signal: AbortSignal, background = false) => {
    if (!token) {
      setRecommendations([]);
      setLoading(false);
      setError(null);
      return;
    }

    if (!background) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await talentSearchApi.getRecommendations(token, RECOMMENDATION_POOL_SIZE, signal);

      if (!signal.aborted) {
        setRecommendations(response.suggestions);
      }
    } catch (loadError) {
      if (!signal.aborted && !isRequestCanceled(loadError)) {
        setError(loadError instanceof Error ? loadError.message : 'Failed to load recommendations');
      }
    } finally {
      if (!signal.aborted && !background) {
        setLoading(false);
      }
    }
  }, [token]);

  const refresh = useCallback((background = false) => {
    refreshControllerRef.current?.abort();
    const controller = new AbortController();
    refreshControllerRef.current = controller;
    void loadRecommendations(controller.signal, background);
  }, [loadRecommendations]);

  useEffect(() => {
    pendingFollowIdsRef.current = {};
    setPendingFollowIds({});
    setFollowError(null);
    refresh(false);

    return () => {
      refreshControllerRef.current?.abort();
      Object.values(followControllersRef.current).forEach((controller) => controller.abort());
      followControllersRef.current = {};
    };
  }, [refresh]);

  const followRecommendation = useCallback(async (userId: string) => {
    if (!token || !userId || pendingFollowIdsRef.current[userId]) {
      return;
    }

    const controller = new AbortController();
    followControllersRef.current[userId] = controller;
    pendingFollowIdsRef.current = {...pendingFollowIdsRef.current, [userId]: true};
    setPendingFollowIds(pendingFollowIdsRef.current);
    setFollowError(null);

    try {
      const response = await followApi.followUser(token, userId, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      if (!response.isFollowing) {
        throw new Error('The follow request did not complete. Please try again.');
      }

      setRecommendations((current) => current.filter((recommendation) => recommendation._id !== response.userId));
      onFollowingCountDelta?.(1);
      refresh(true);
    } catch (actionError) {
      if (!controller.signal.aborted) {
        setFollowError(actionError instanceof Error ? actionError.message : 'Failed to follow this member');
      }
    } finally {
      delete followControllersRef.current[userId];

      if (!controller.signal.aborted) {
        const nextPendingFollowIds = {...pendingFollowIdsRef.current};
        delete nextPendingFollowIds[userId];
        pendingFollowIdsRef.current = nextPendingFollowIds;
        setPendingFollowIds(nextPendingFollowIds);
      }
    }
  }, [onFollowingCountDelta, refresh, token]);

  return {
    suggestions: recommendations.slice(0, VISIBLE_RECOMMENDATION_COUNT),
    suggestionsLoading: loading,
    suggestionsError: error,
    suggestionFollowError: followError,
    pendingSuggestionFollowIds: pendingFollowIds,
    retrySuggestions: () => refresh(false),
    dismissSuggestionFollowError: () => setFollowError(null),
    followSuggestion: followRecommendation,
  };
};
